/**
 * ProcedureDetail page — shows the current published body of a single
 * procedure with an "I've read this" acknowledgement button. Managers
 * see extra controls (edit meta, draft new version, publish, archive)
 * and a compliance section listing who has / hasn't acknowledged.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, BookOpen, CheckCircle2, Loader2, Edit3, Send, Archive,
  History, Users as UsersIcon, Eye, PencilLine,
} from "lucide-react";

export default function ProcedureDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isManager = user && ["owner", "manager"].includes(user.role);

  const [proc, setProc] = useState(null);
  const [versions, setVersions] = useState([]);
  const [myAck, setMyAck] = useState(null);      // { acknowledged: bool, ack }
  const [status, setStatus] = useState(null);    // compliance status of current version
  const [loading, setLoading] = useState(true);
  const [acking, setAcking] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDraft, setShowDraft] = useState(false);
  const [viewVersionId, setViewVersionId] = useState(null); // preview an older version

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rProc, rVer] = await Promise.all([
        api.get(`/procedures/${id}`),
        api.get(`/procedures/${id}/versions`),
      ]);
      setProc(rProc.data);
      setVersions(rVer.data || []);
      const curV = rProc.data?.current_published_version;
      if (curV) {
        const [rAck, rSt] = await Promise.all([
          api.get(`/procedures/${id}/my-acknowledgement`, { params: { version: curV } }),
          isManager
            ? api.get(`/procedures/${id}/acknowledgement-status`, { params: { version: curV } })
            : Promise.resolve({ data: null }),
        ]);
        setMyAck(rAck.data);
        setStatus(rSt.data);
      } else {
        setMyAck(null);
        setStatus(null);
      }
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [id, isManager]);

  useEffect(() => { load(); }, [load]);

  const currentPublished = useMemo(() => {
    if (!proc?.current_published_version) return null;
    return versions.find(
      (v) => v.version === proc.current_published_version && v.status === "published",
    ) || null;
  }, [proc, versions]);

  const viewedVersion = useMemo(() => {
    if (viewVersionId) return versions.find((v) => v.id === viewVersionId) || currentPublished;
    return currentPublished;
  }, [viewVersionId, versions, currentPublished]);

  const latestDraft = useMemo(
    () => versions.find((v) => v.status === "draft") || null,
    [versions],
  );

  async function acknowledge() {
    if (!currentPublished) return;
    setAcking(true);
    try {
      await api.post(
        `/procedures/${id}/acknowledge`,
        {},
        { params: { version: currentPublished.version } },
      );
      toast.success(`Acknowledged v${currentPublished.version}`);
      await load();
    } catch (e) {
      toast.error("Failed to acknowledge", { description: e?.response?.data?.detail || e.message });
    } finally {
      setAcking(false);
    }
  }

  async function archive() {
    if (!window.confirm("Archive this procedure? Staff won't see it in the library.")) return;
    try {
      await api.delete(`/procedures/${id}`);
      toast.success("Archived");
      nav("/procedures");
    } catch (e) {
      toast.error("Archive failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function publishDraft(v) {
    if (!window.confirm(`Publish v${v.version}? All staff will be asked to acknowledge.`)) return;
    try {
      await api.post(`/procedures/versions/${v.id}/publish`, {});
      toast.success(`v${v.version} published — staff have been notified.`);
      await load();
    } catch (e) {
      toast.error("Publish failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-sm text-zinc-500 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (!proc) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="p-8 text-center border border-dashed border-zinc-200 rounded-md text-sm text-zinc-500">
          Procedure not found.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4" data-testid={`procedure-detail-${id}`}>
      <div>
        <Link
          to="/procedures"
          className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"
          data-testid="procedure-back"
        >
          <ArrowLeft className="h-3 w-3" /> Back to procedures
        </Link>
      </div>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-950 flex items-center gap-2">
            <BookOpen className="h-6 w-6" /> {proc.title}
          </h1>
          <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-zinc-500">
            {proc.category && (
              <span className="uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 border border-zinc-200 text-2xs">
                {proc.category}
              </span>
            )}
            {proc.current_published_version ? (
              <span>Current version <b className="text-zinc-800">v{proc.current_published_version}</b></span>
            ) : (
              <span>No published version yet</span>
            )}
          </div>
          {proc.description && (
            <p className="mt-2 text-sm text-zinc-600 max-w-2xl">{proc.description}</p>
          )}
        </div>
        {isManager && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setShowEdit(true)}
              data-testid="procedure-edit-meta-btn"
              className="text-xs"
            >
              <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit details
            </Button>
            <Button
              onClick={() => setShowDraft(true)}
              className="bg-zinc-950 hover:bg-zinc-800 text-white text-xs"
              data-testid="procedure-new-version-btn"
            >
              <PencilLine className="h-3.5 w-3.5 mr-1" /> {latestDraft ? "Edit draft" : "New version"}
            </Button>
            <Button
              variant="outline"
              onClick={archive}
              data-testid="procedure-archive-btn"
              className="text-xs text-red-700 hover:bg-red-50"
            >
              <Archive className="h-3.5 w-3.5 mr-1" /> Archive
            </Button>
          </div>
        )}
      </div>

      {/* Acknowledgement banner (staff or manager) */}
      {currentPublished && (
        <Card className="p-4 rounded-md shadow-none border border-zinc-200">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm">
              {myAck?.acknowledged ? (
                <span className="text-emerald-800 flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4" />
                  You acknowledged v{currentPublished.version} on{" "}
                  <b>{myAck.ack?.acknowledged_at ? new Date(myAck.ack.acknowledged_at).toLocaleString() : "—"}</b>
                </span>
              ) : (
                <span className="text-amber-900">
                  You have <b>not yet</b> acknowledged v{currentPublished.version}.
                </span>
              )}
            </div>
            {!myAck?.acknowledged && (
              <Button
                onClick={acknowledge}
                disabled={acking}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="procedure-ack-btn"
              >
                {acking
                  ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</>
                  : <><CheckCircle2 className="h-4 w-4 mr-1" /> I&apos;ve read this</>}
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Draft available (manager view) */}
      {isManager && latestDraft && (
        <Card className="p-4 rounded-md shadow-none border border-purple-200 bg-purple-50">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-purple-900">
              Draft <b>v{latestDraft.version}</b> is ready to publish.
              {latestDraft.change_note && <span className="ml-2 italic text-purple-700">— {latestDraft.change_note}</span>}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setViewVersionId(latestDraft.id)}
                className="text-xs"
                data-testid="procedure-preview-draft-btn"
              >
                <Eye className="h-3.5 w-3.5 mr-1" /> Preview draft
              </Button>
              <Button
                onClick={() => publishDraft(latestDraft)}
                className="bg-zinc-950 hover:bg-zinc-800 text-white text-xs"
                data-testid={`procedure-publish-${latestDraft.id}`}
              >
                <Send className="h-3.5 w-3.5 mr-1" /> Publish
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Body viewer */}
      {viewedVersion ? (
        <Card className="p-6 rounded-md shadow-none border border-zinc-200" data-testid="procedure-body">
          <div className="flex items-center justify-between mb-3">
            <div className="text-2xs uppercase tracking-wider text-zinc-500">
              Viewing v{viewedVersion.version} · {viewedVersion.status}
              {viewedVersion.published_at && ` · published ${new Date(viewedVersion.published_at).toLocaleDateString()}`}
            </div>
            {viewVersionId && viewedVersion.id !== currentPublished?.id && (
              <button
                onClick={() => setViewVersionId(null)}
                className="text-xs text-zinc-500 hover:text-zinc-900 underline"
                data-testid="procedure-view-current"
              >
                Back to current published version
              </button>
            )}
          </div>
          <MarkdownBody body={viewedVersion.body_markdown} />
        </Card>
      ) : (
        <Card className="p-6 rounded-md shadow-none border-2 border-dashed border-zinc-200 bg-zinc-50 text-sm text-zinc-500">
          {isManager
            ? "No version yet. Click ‘New version’ to draft the first one."
            : "No content published yet — check back soon."}
        </Card>
      )}

      {/* Version history */}
      {versions.length > 0 && (
        <Card className="p-4 rounded-md shadow-none border border-zinc-200">
          <div className="text-2xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5 mb-2">
            <History className="h-3.5 w-3.5" /> Version history
          </div>
          <ul className="divide-y divide-zinc-100">
            {versions.map((v) => (
              <li
                key={v.id}
                className="py-2 flex items-center justify-between gap-2 flex-wrap text-sm"
                data-testid={`procedure-version-${v.id}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono-num">v{v.version}</span>
                  <span className={`text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                    v.status === "published"
                      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                      : v.status === "draft"
                      ? "bg-purple-50 text-purple-800 border-purple-200"
                      : "bg-zinc-100 text-zinc-600 border-zinc-200"
                  }`}>{v.status}</span>
                  {v.change_note && <span className="text-xs text-zinc-600 italic">— {v.change_note}</span>}
                  {v.published_at && (
                    <span className="text-2xs text-zinc-500">
                      · published {new Date(v.published_at).toLocaleString()}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setViewVersionId(v.id)}
                  className="text-xs text-zinc-600 hover:text-zinc-900 underline"
                  data-testid={`procedure-view-${v.id}`}
                >
                  View
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Compliance (manager only) */}
      {isManager && currentPublished && status && (
        <Card className="p-4 rounded-md shadow-none border border-zinc-200" data-testid="procedure-compliance">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="text-2xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
              <UsersIcon className="h-3.5 w-3.5" /> Compliance — v{currentPublished.version}
            </div>
            <div className="text-xs text-zinc-600">
              <b className="text-zinc-900">{status.acknowledged_count}</b> of{" "}
              <b className="text-zinc-900">{status.total_users}</b> staff have acknowledged
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-2xs text-emerald-800 font-medium mb-1">
                Acknowledged ({status.acknowledged.length})
              </div>
              <ul className="text-xs space-y-1">
                {status.acknowledged.length === 0 && <li className="text-zinc-500 italic">Nobody yet.</li>}
                {status.acknowledged.map((u) => (
                  <li key={u.user_id} className="text-zinc-800" data-testid={`compliance-ack-${u.user_id}`}>
                    <CheckCircle2 className="h-3 w-3 inline mr-1 text-emerald-700" />
                    {u.name}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-2xs text-amber-900 font-medium mb-1">
                Still pending ({status.pending.length})
              </div>
              <ul className="text-xs space-y-1">
                {status.pending.length === 0 && <li className="text-zinc-500 italic">Everyone&apos;s up to date. 🎉</li>}
                {status.pending.map((u) => (
                  <li key={u.user_id} className="text-zinc-800" data-testid={`compliance-pending-${u.user_id}`}>
                    <span className="inline-block h-2 w-2 rounded-full bg-amber-500 mr-1.5" />
                    {u.name} <span className="text-zinc-400 font-mono-num">· {u.email}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {showEdit && (
        <EditMetaDialog
          proc={proc}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); load(); }}
        />
      )}
      {showDraft && (
        <DraftVersionDialog
          procId={id}
          existingDraft={latestDraft}
          currentBody={currentPublished?.body_markdown || ""}
          onClose={() => setShowDraft(false)}
          onSaved={() => { setShowDraft(false); load(); }}
        />
      )}
    </div>
  );
}


function MarkdownBody({ body }) {
  if (!body) {
    return <div className="text-sm text-zinc-500 italic">No content.</div>;
  }
  return (
    <div className="prose prose-sm max-w-none prose-headings:text-zinc-950 prose-p:text-zinc-800 prose-li:text-zinc-800 prose-strong:text-zinc-950 prose-a:text-blue-700">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
    </div>
  );
}


function EditMetaDialog({ proc, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: proc.title || "",
    category: proc.category || "",
    description: proc.description || "",
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      await api.patch(`/procedures/${proc.id}`, {
        title: form.title.trim(),
        category: form.category.trim() || null,
        description: form.description,
      });
      toast.success("Saved");
      onSaved?.();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-procedure-dialog">
        <DialogHeader>
          <DialogTitle>Edit procedure</DialogTitle>
          <DialogDescription className="text-xs">
            Update the name, category or description. To change the SOP
            content, use <b>New version</b> instead.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-2xs">Title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="edit-procedure-title"
            />
          </div>
          <div>
            <Label className="text-2xs">Category</Label>
            <Input
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="edit-procedure-category"
            />
          </div>
          <div>
            <Label className="text-2xs">Description</Label>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="edit-procedure-description"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-zinc-950 text-white" data-testid="edit-procedure-save">
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function DraftVersionDialog({ procId, existingDraft, currentBody, onClose, onSaved }) {
  const [body, setBody] = useState(existingDraft?.body_markdown ?? currentBody ?? "");
  const [changeNote, setChangeNote] = useState(existingDraft?.change_note ?? "");
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  async function saveDraft(publishAfter = false) {
    if (!body.trim()) return toast.error("Body cannot be empty");
    const which = publishAfter ? setPublishing : setSaving;
    which(true);
    try {
      // Backend always creates a *new* draft version — even if editing an
      // existing draft. Simple and matches the versioning model.
      const r = await api.post(`/procedures/${procId}/versions`, {
        body_markdown: body,
        change_note: changeNote,
      });
      if (publishAfter) {
        await api.post(`/procedures/versions/${r.data.id}/publish`, {});
        toast.success(`v${r.data.version} published`);
      } else {
        toast.success(`Draft v${r.data.version} saved`);
      }
      onSaved?.();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { which(false); }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="draft-version-dialog">
        <DialogHeader>
          <DialogTitle>{existingDraft ? `Edit draft v${existingDraft.version}` : "New draft version"}</DialogTitle>
          <DialogDescription className="text-xs">
            Markdown supported. Include headings, bullet points and any
            step-by-step instructions. Publishing notifies every staff
            member and resets acknowledgements.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-2xs">Content (markdown)</Label>
            <button
              onClick={() => setPreview((v) => !v)}
              className="text-xs text-zinc-600 hover:text-zinc-900 underline"
              data-testid="draft-preview-toggle"
            >
              {preview ? "Edit" : "Preview"}
            </button>
          </div>
          {preview ? (
            <div className="min-h-[280px] border border-zinc-200 rounded-md p-3 bg-zinc-50 max-h-[420px] overflow-y-auto">
              <MarkdownBody body={body} />
            </div>
          ) : (
            <Textarea
              rows={14}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="font-mono-num text-sm"
              placeholder={"## Overview\n\nDescribe the procedure here.\n\n### Steps\n\n1. First step\n2. Second step"}
              data-testid="draft-body"
            />
          )}
          <div>
            <Label className="text-2xs">Change note (optional)</Label>
            <Input
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value)}
              placeholder="What changed since the last version?"
              data-testid="draft-change-note"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant="outline"
            onClick={() => saveDraft(false)}
            disabled={saving || publishing}
            data-testid="draft-save-btn"
          >
            {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</> : "Save draft"}
          </Button>
          <Button
            onClick={() => saveDraft(true)}
            disabled={saving || publishing}
            className="bg-zinc-950 text-white"
            data-testid="draft-publish-btn"
          >
            {publishing ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Publishing…</> : <><Send className="h-4 w-4 mr-1" /> Save & publish</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
